import React, { useState, useRef, useEffect } from 'react';
import api from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';

function normalizeAnswer(str) {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) dp[i][j] = dp[i - 1][j - 1];
      else dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

const MAX_LEVENSHTEIN_DISTANCE = 2;

export default function FillViewer({ cards, index, onNext, onPrev, onResult, weakMode, showCharCount, onMark, markedCardIds }) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const inputRef = useRef(null);

  const card = cards[index];

  useEffect(() => {
    setInputValue('');
    setSubmitted(false);
    setIsCorrect(null);
    if (inputRef.current) inputRef.current.focus();
  }, [index]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const userNorm = normalizeAnswer(inputValue);
    const correctNorm = normalizeAnswer(card.answer);
    let correct;
    if (weakMode) {
      correct = userNorm === correctNorm || levenshtein(userNorm, correctNorm) <= MAX_LEVENSHTEIN_DISTANCE;
    } else {
      correct = userNorm === correctNorm;
    }
    setIsCorrect(correct);
    setSubmitted(true);
    try {
      await api.post(`/statistics/cards/${card.id}`, { correct });
    } catch (err) {
      console.error('Failed to record card statistic:', err);
    }
  };

  const handleNext = () => {
    if (onResult) onResult(isCorrect);
    onNext();
  };

  const handleMark = async () => {
    try {
      await api.post(`/statistics/cards/${card.id}/mark`);
      if (onMark) onMark(card.id);
    } catch (_) {}
  };

  const isMarked = markedCardIds && markedCardIds.has(card.id);

  return (
    <div className="flex flex-col items-center gap-6">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {t('viewer.cardOf', { n: index + 1, total: cards.length })}
        {weakMode && <span className="ml-2 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded px-2 py-0.5">{t('viewer.weak')}</span>}
      </p>

      <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-400 dark:text-indigo-300 mb-1">{t('viewer.question')}</p>
          <p className="text-xl font-semibold text-gray-800 dark:text-gray-100">{card.question}</p>
        </div>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t('viewer.typeAnswer')}
              className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              autoComplete="off"
            />
            {showCharCount && (
              <p className="text-xs text-gray-400 dark:text-gray-500 text-right">
                {t('viewer.charCount', { n: inputValue.length, total: card.answer.trim().length })}
              </p>
            )}
            <button
              type="submit"
              disabled={!inputValue.trim()}
              className="w-full px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('viewer.checkAnswer')}
            </button>
          </form>
        ) : (
          <div className={`rounded-xl p-4 ${isCorrect ? 'bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800'}`} aria-live="polite">
            <p className={`text-sm font-semibold ${isCorrect ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
              {isCorrect ? t('viewer.correct') : t('viewer.incorrect')}
            </p>
            {!isCorrect && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {t('viewer.correctAnswer')}   <span className="font-semibold">{card.answer}</span>
              </p>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('viewer.yourAnswer')} {inputValue}</p>
            {!isCorrect && card.context && (
              <p className="text-sm text-blue-600 dark:text-blue-400 mt-2 italic">💡 {card.context}</p>
            )}
          </div>
        )}
      </div>

      {submitted && (
        <button
          onClick={handleNext}
          className="px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition"
        >
          {index === cards.length - 1 ? t('viewer.finish') : t('viewer.next')}
        </button>
      )}

      <div className="flex gap-4">
        <button
          onClick={onPrev}
          disabled={index === 0}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 transition"
        >
          {t('viewer.prev')}
        </button>
        <button
          onClick={handleMark}
          aria-label={isMarked ? t('viewer.unpinCard') : t('viewer.pinCard')}
          title={isMarked ? t('viewer.unpinCard') : t('viewer.pinCard')}
          className={`px-4 py-2 font-semibold rounded-lg border transition ${
            isMarked
              ? 'bg-orange-200 dark:bg-orange-900 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700 hover:bg-orange-300 dark:hover:bg-orange-800'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          {isMarked ? t('viewer.pinned') : t('viewer.pin')}
        </button>
      </div>
    </div>
  );
}
